import { Smartphone, Check } from 'lucide-react';

interface MobileShowcaseProps {
  onStartFree: () => void;
}

export function MobileShowcase({ onStartFree }: MobileShowcaseProps) {
  return (
    <section className="py-24 bg-slate-900 overflow-hidden relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-coral-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sage-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-8 bg-gradient-to-tr from-coral-500/20 to-sage-500/20 rounded-[3rem] blur-2xl" />

              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop&crop=face"
                  alt="Person using VZMO on their phone"
                  className="rounded-3xl shadow-2xl w-full aspect-[3/4] object-cover"
                />

                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[160px] sm:w-[180px]">
                  <div className="bg-slate-800 rounded-[2rem] p-1.5 shadow-2xl border border-slate-700">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-slate-800 rounded-b-xl z-20" />
                    <div className="bg-[#FDFBF7] rounded-[1.5rem] overflow-hidden">
                      <div className="bg-white border-b border-slate-100 px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-4 h-4 rounded-full bg-coral-500 flex items-center justify-center">
                            <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8 12h.01M12 12h.01M16 12h.01" />
                            </svg>
                          </div>
                          <span className="text-[8px] font-bold text-slate-900">VZMO</span>
                        </div>
                      </div>

                      <div className="p-2 space-y-1.5">
                        <div className="bg-white rounded-lg p-2 shadow-sm border border-slate-100">
                          <p className="text-[7px] text-slate-500 mb-0.5">You</p>
                          <p className="text-[8px] text-slate-700 leading-relaxed">
                            Feeling overwhelmed today...
                          </p>
                        </div>

                        <div className="bg-coral-50 rounded-lg p-2 border border-coral-100">
                          <p className="text-[7px] text-coral-600 mb-0.5">Coach</p>
                          <p className="text-[8px] text-slate-700 leading-relaxed">
                            Let's break this down. What's the one thing you can do right now?
                          </p>
                        </div>

                        <div className="flex items-center justify-center gap-1 pt-1">
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </div>
                          <div className="w-6 h-6 rounded-full bg-coral-500 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white text-sm font-medium mb-6">
              <Smartphone className="h-4 w-4" />
              <span>Mobile-first design</span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Your coach is always
              <span className="block text-coral-400">in your pocket</span>
            </h2>

            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0">
              Designed for real life. Quick check-ins on your commute. Deep conversations when you need them. Works beautifully on any device.
            </p>

            <div className="space-y-4 mb-10">
              {[
                'Works offline - journal anytime, anywhere',
                'Native mobile experience - fast and responsive',
                'Private and secure - your data stays yours'
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-6 h-6 rounded-full bg-sage-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3.5 w-3.5 text-sage-400" />
                  </div>
                  <span className="text-slate-200">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={onStartFree}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold text-lg rounded-full hover:bg-slate-100 transition-all duration-200 shadow-lg"
            >
              Try it free
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
